/**
 * Selection model for grouped add-ons.
 *
 * A cart item's add-on selection is namespaced BY GROUP so the same service
 * slug can appear in multiple groups without their selections bleeding into one
 * another:
 *
 *   item.selectedAddOns = { [groupKey]: { [optionSlug]: { quantity } } }
 *
 * Every reader/writer goes through these pure helpers — no component touches the
 * raw shape, which is exactly what previously caused the slug-keyed cross-group
 * bleed (selecting a service in one group marking it in another).
 */

export function isSelected(selection, groupKey, slug) {
  return !!selection?.[groupKey]?.[slug];
}

export function selectedSlugsInGroup(selection, groupKey) {
  return Object.keys(selection?.[groupKey] || {});
}

export function countInGroup(selection, groupKey) {
  return selectedSlugsInGroup(selection, groupKey).length;
}

export function getQuantity(selection, groupKey, slug, fallback = 1) {
  const q = selection?.[groupKey]?.[slug]?.quantity;
  return q != null ? q : fallback;
}

/** Flatten the whole selection into `{ groupKey, slug, quantity }` records. */
export function allSelected(selection) {
  const out = [];
  Object.entries(selection || {}).forEach(([groupKey, opts]) => {
    Object.entries(opts || {}).forEach(([slug, meta]) => {
      out.push({ groupKey, slug, quantity: meta?.quantity });
    });
  });
  return out;
}

/**
 * Replace ONE group's selected slugs, preserving any existing per-slug quantity.
 * Removing the last slug drops the group key entirely.
 */
export function withGroupSelection(selection, groupKey, slugs) {
  const prevGroup = selection?.[groupKey] || {};
  const next = { ...(selection || {}) };
  if (!slugs || slugs.length === 0) {
    delete next[groupKey];
    return next;
  }
  const nextGroup = {};
  slugs.forEach((slug) => {
    nextGroup[slug] = prevGroup[slug] || {};
  });
  next[groupKey] = nextGroup;
  return next;
}

/** Set the buyer-chosen quantity for an already-selected (groupKey, slug). */
export function withQuantity(selection, groupKey, slug, quantity) {
  if (!selection?.[groupKey]?.[slug]) return selection; // only on a selected option
  const q = Math.max(1, Math.floor(Number(quantity) || 1));
  return {
    ...selection,
    [groupKey]: {
      ...selection[groupKey],
      [slug]: { ...selection[groupKey][slug], quantity: q },
    },
  };
}

/** Union two selections group-by-group (used when re-adding a service to cart). */
export function mergeSelection(a, b) {
  const out = { ...(a || {}) };
  Object.entries(b || {}).forEach(([groupKey, opts]) => {
    out[groupKey] = { ...(out[groupKey] || {}), ...opts };
  });
  return out;
}
